import {createContext, useState, useEffect, useContext, FC, useCallback} from 'react';
import {UserInfo} from '@vkontakte/vk-bridge';
import {isDev, USER_ID_FROM} from 'utils/constants';
import {expandUserInfoWithDataFromSdk, isMailClient, isOKClient} from './utils';
import {captureMessage} from '@sentry/react';
import {useSearchParams} from './searchParamsContext';
import {bridge} from './bridge';
import {VKWebAppGetCustomSdkUserInfoResult} from './types';
import {fetchUserInfo} from './requests';

export type FullUserInfo = {
    general?: Partial<Omit<UserInfo, 'id'>> & {
        vk_id?: number;
        ok_id?: number;
    };
    email?: string;
    email_hash?: string;
    phone?: string;
    phone_hash?: string;
    email_data?: VKWebAppGetCustomSdkUserInfoResult['decode_params'];
    phone_data?: VKWebAppGetCustomSdkUserInfoResult['decode_params'];
};

const UserInfoContext = createContext<FullUserInfo>({});

type userPhoneEmailData = Omit<FullUserInfo, 'general' | 'access_token'>;

type UserInfoContextCreaterProps = {
    children?: React.ReactNode;
    isFilledPreviously?: boolean;
};

// const customSdkInfo = {
//     first_name: 'Вася',
//     city: 'Москва',
//     country: 'Россия',
//     phone: '7325***1421',
//     phone_sign: 'afsfnejkwnfoe',
//     email: 'fasf@mail.ru',
//     email_sign: 'afsfnejkwnfoe',
//     bdate: '02.06.1995',
//     decode_params: {
//         access_token: 'access_token_test',
//         app_id: 'mail-mail',
//     },
//     vk_id: 147758631,
// } satisfies VKWebAppGetCustomSdkUserInfoResult;

export const UserInfoContextCreater: FC<UserInfoContextCreaterProps> = ({children, isFilledPreviously}) => {
    const [userInfo, setUserInfo] = useState<FullUserInfo>({});
    const {vk_app_id, vk_client, formDataHash} = useSearchParams();

    const fillBySdk = useCallback(async () => {
        if (!bridge) return;
        try {
            const customSdkInfo = (await bridge.sendWithCatch(
                // @ts-ignore // тк кастомный евент
                'VKWebAppGetCustomSdkUserInfo',
            )) as VKWebAppGetCustomSdkUserInfoResult;

            // дока по интеграции сдк - https://confluence.vk.team/pages/viewpage.action?pageId=1554171723
            if (customSdkInfo.vk_id) {
                try {
                    // если смогли вытащить данные из вк
                    const vkInfo = await fetchUserInfo(
                        new URLSearchParams({
                            lead_form_data: formDataHash,
                            vk_id: String(customSdkInfo.vk_id),
                        }).toString(),
                    );
                    // подмешиваем недостающее из вк и заполняем
                    setUserInfo((userInfo) => expandUserInfoWithDataFromSdk(userInfo, customSdkInfo, vkInfo));
                } catch (e) {
                    // иначе заполняем из сдк
                    setUserInfo((userInfo) => expandUserInfoWithDataFromSdk(userInfo, customSdkInfo));
                }
                return;
            }
            // window.addDebugData(`sdkInfo: ${JSON.stringify(customSdkInfo)}`);
            setUserInfo((userInfo) => expandUserInfoWithDataFromSdk(userInfo, customSdkInfo));
        } catch (e) {}
    }, [formDataHash]);

    const fillCallback = useCallback(async () => {
        // если форму уже заполняли - не запрашиваем контактные данные
        if (!bridge || isFilledPreviously) return;

        window.addDebugData(`vk_client: ${vk_client}`);
        if (vk_client === USER_ID_FROM.SDK) {
            // для внешних сдк идем по отдельному пути
            fillBySdk();
            return;
        }

        let userId: string | null = null;

        if (bridge.supports('VKWebAppGetUserInfo')) {
            await bridge
                .sendWithCatch('VKWebAppGetUserInfo')
                .then((general) => {
                    userId = String(general.id);
                    setUserInfo((userInfo) => ({
                        ...userInfo,
                        general: {
                            ...general,
                            ...(vk_client === USER_ID_FROM.VK ? {vk_id: general.id} : {}),
                            ...(vk_client === USER_ID_FROM.OK ? {ok_id: general.id} : {}),
                        },
                    }));
                })
                .catch(() => {});
        }

        if (bridge.supports('VKWebAppGetAuthToken') && vk_app_id) {
            if (!isDev || (isDev && bridge.isEmbedded())) {
                bridge
                    .sendWithCatch('VKWebAppGetAuthToken', {
                        app_id: Number(vk_app_id),
                        scope: '',
                    })
                    .then(({access_token}) => {
                        if (!bridge) return;

                        setUserInfo((userInfo) => ({
                            ...userInfo,
                            access_token,
                        }));

                        const params = {v: '5.81', access_token};

                        if (!bridge.supports('VKWebAppCallAPIMethod')) {
                            captureMessage('VKWebAppCallAPIMethod not supported', 'error');
                            return;
                        }

                        if (isMailClient()) {
                            bridge
                                .sendWithCatch('VKWebAppCallAPIMethod', {
                                    method: 'leadForms.getUserFormData',
                                    request_id: 'email',
                                    params,
                                })
                                .then(({response}) => {
                                    if (typeof response !== 'object') {
                                        captureMessage('Failed parse MAIL data', 'error');
                                        return;
                                    }

                                    let data: FullUserInfo = {};

                                    if (response.email && typeof response.email === 'string') {
                                        // email от mail.ru всегда не маскированный
                                        data.email = response.email;
                                    }

                                    if (response.phone && typeof response.phone === 'string') {
                                        data = {
                                            ...data,
                                            phone: response.phone,
                                            phone_hash: 'hash', // по наличию phone_hash определяем надо ли предзаполнять, поэтому вставляем заглушку (любую не пустую строку)
                                            phone_data: {
                                                access_token: access_token,
                                                app_id: USER_ID_FROM.MAIL,
                                            },
                                        };
                                    }

                                    setUserInfo((userInfo) => ({
                                        ...userInfo,
                                        ...data,
                                    }));
                                })
                                .catch(() => {});
                        } else if (isOKClient()) {
                            // для ок
                            // заглушка под заполнение из оков
                            bridge
                                .sendWithCatch('VKWebAppCallAPIMethod', {
                                    method: 'account.getMaskedEmail',
                                    request_id: 'okmail',
                                    params,
                                })
                                .then(({response}) => {
                                    if (typeof response !== 'object') {
                                        captureMessage('Failed parse OK email data', 'error');
                                        return;
                                    }

                                    if (
                                        response.email &&
                                        typeof response.email === 'string' &&
                                        response.sign &&
                                        typeof response.sign === 'string'
                                    ) {
                                        setUserInfo((userInfo) => ({
                                            ...userInfo,
                                            email: response.email,
                                            email_hash: response.sign,
                                            email_data: {
                                                access_token: access_token,
                                                app_id: USER_ID_FROM.OK,
                                                ...(userId ? {user_id: userId} : {}),
                                            },
                                        }));
                                    }
                                })
                                .catch(() => {});

                            bridge
                                .sendWithCatch('VKWebAppCallAPIMethod', {
                                    method: 'account.getMaskedPhone',
                                    request_id: 'okphone',
                                    params,
                                })
                                .then(({response}) => {
                                    if (typeof response !== 'object') {
                                        captureMessage('Failed parse OK phone data', 'error');
                                        return;
                                    }

                                    if (
                                        response.phone_number &&
                                        typeof response.phone_number === 'string' &&
                                        response.sign &&
                                        typeof response.sign === 'string'
                                    ) {
                                        setUserInfo((userInfo) => ({
                                            ...userInfo,
                                            phone: response.phone_number,
                                            phone_hash: response.sign,
                                            phone_data: {
                                                access_token: access_token,
                                                app_id: USER_ID_FROM.OK,
                                                ...(userId ? {user_id: userId} : {}),
                                            },
                                        }));
                                    }
                                })
                                .catch(() => {});
                        } else {
                            // для вк
                            bridge
                                .sendWithCatch('VKWebAppCallAPIMethod', {
                                    method: 'leadForms.getUserFormData',
                                    request_id: 'vkGetUserFormData',
                                    params,
                                })
                                .then(({response}) => {
                                    const userData: userPhoneEmailData = {};

                                    if (response.email_masked && typeof response.email_masked === 'string') {
                                        userData.email = response.email_masked;
                                        userData.email_hash = response.email_hash;
                                        userData.email_data = {
                                            access_token: access_token,
                                            app_id: USER_ID_FROM.VK,
                                            ...(userId ? {user_id: userId} : {}),
                                        };
                                    }

                                    if (response.phone_masked && typeof response.phone_masked === 'string') {
                                        userData.phone = response.phone_masked;
                                        userData.phone_hash = response.phone_hash;
                                        userData.phone_data = {
                                            access_token: access_token,
                                            app_id: USER_ID_FROM.VK,
                                            ...(userId ? {user_id: userId} : {}),
                                        };
                                    }

                                    if (Object.keys(userData).length) {
                                        setUserInfo((userInfo) => ({...userInfo, ...userData}));
                                    }
                                })
                                .catch(() => {});
                        }
                    });
            } else {
                setUserInfo((userInfo) => ({
                    ...userInfo,
                    email: 'dev@email.ru',
                    // email_hash: 'devemailhash',
                    // phone: '7********77', // vk формат
                    phone: '7 (926) 494-**-**', // почта формат
                    phone_hash: 'devphonehash',
                    access_token: 'access_token_test',
                }));
            }
        }
    }, [vk_app_id, vk_client, fillBySdk, isFilledPreviously]);

    useEffect(() => {
        fillCallback();
    }, [fillCallback]);

    return (
        <UserInfoContext.Provider
            value={userInfo}
            children={children}
        />
    );
};

export const useUserInfo = () => useContext(UserInfoContext);
